import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FilmStrip, CaretRight, TrendUp, Users, Shield, Play, Heart } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FAQS = [
  {
    q: 'What is Fanzingy?',
    a: 'Fanzingy is a marketplace where Investors, Producers, and Fans come together around films. Producers raise capital, investors earn royalty rights on box-office revenue, and fans discover, discuss, and back the movies they love — all from one cinematic dashboard.',
  },
  {
    q: 'How do royalties work?',
    a: "Every movie comes with investment tiers (e.g. Silver / Gold / Executive Producer). Each tier has a royalty percentage. When the movie generates revenue, producers distribute royalties, and your share lands automatically against your investment.",
  },
  {
    q: 'Which film industries are supported?',
    a: 'Bollywood, Hollywood, Tollywood and Kollywood — with industry-specific fan communities so you can join the right conversation, theory threads, and casting debates.',
  },
  {
    q: 'How much can I start investing with?',
    a: 'It depends on the movie. Producers set their own tiers — some open at as little as ₹50,000, others reserve seats for executive producers at ₹50,00,000+. Browse the marketplace to see live options.',
  },
  {
    q: 'Can I cancel or change anytime?',
    a: 'You can stop browsing or back out before committing to any tier. Once an investment is finalised on a movie, your royalty position is locked for that title until producer distribution events.',
  },
  {
    q: 'How does the fan community work?',
    a: 'Anyone with a Fanzingy account can post in the community — by industry or by movie. Like posts, leave comments, follow theories, and discover what other cinephiles are saying before you invest or watch.',
  },
];

const REASONS = [
  {
    title: 'Earn royalties on hits',
    desc: 'Back the next blockbuster. Receive automatic royalty distributions as movies generate revenue.',
    icon: TrendUp,
  },
  {
    title: 'Industry-native communities',
    desc: 'Separate hubs for Tollywood, Bollywood, Kollywood and Hollywood. Talk about what you love, with people who get it.',
    icon: Users,
  },
  {
    title: 'Producer-grade transparency',
    desc: 'Live funding bars, tier breakdowns, revenue updates. No black-box accounting.',
    icon: Shield,
  },
  {
    title: 'Discover before you back',
    desc: 'Watch funding momentum, read fan theories, and pick films that match your taste.',
    icon: Heart,
  },
];

const FALLBACK_POSTERS = [
  'https://images.unsplash.com/photo-1485846234645-a62644f84728?w=800&q=80',
  'https://images.unsplash.com/photo-1542204165-65bf26472b9b?w=800&q=80',
  'https://images.unsplash.com/photo-1440404653325-ab127d49abc1?w=800&q=80',
  'https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=800&q=80',
  'https://images.unsplash.com/photo-1518676590629-3dcba9c5a555?w=800&q=80',
  'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=800&q=80',
];

const Landing = ({ user }) => {
  const navigate = useNavigate();
  const [movies, setMovies] = useState([]);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/movies`)
      .then((res) => setMovies(res.data || []))
      .catch(() => setMovies([]));
  }, []);  // run once on mount; BACKEND_URL is module-level constant

  const dashboardRoute = () => {
    if (user?.role === 'admin') return '/admin-dashboard';
    if (user?.role === 'producer') return '/producer-dashboard';
    if (user?.role === 'fan') return '/fan-dashboard';
    return '/investor-dashboard';
  };

  const heroPosters =
    movies.length >= 6
      ? movies.slice(0, 9).map((m) => m.poster_url)
      : [...movies.map((m) => m.poster_url), ...FALLBACK_POSTERS].slice(0, 9);

  const trending = movies.slice(0, 10);

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <div className="noise-overlay" />

      {/* Hero */}
      <section className="relative">
        {/* Backdrop poster grid */}
        <div
          className="absolute inset-0 grid grid-cols-3 md:grid-cols-3 gap-1 opacity-30"
          aria-hidden="true"
          data-testid="hero-poster-grid"
        >
          {heroPosters.map((src, i) => (
            <div
              key={`${src}-${i}`}
              className="bg-cover bg-center"
              style={{ backgroundImage: `url(${src})` }}
            />
          ))}
        </div>
        {/* Vignette overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-background" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/60" />

        {/* Top Nav */}
        <nav data-web-chrome="nav" className="relative z-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <img src="/fanzingy-logo.jpeg" alt="FanZingy" className="h-10 w-10 rounded-full object-cover ring-1 ring-white/10" data-testid="brand-logo" />
            <span className="font-heading text-2xl font-bold tracking-tight">Fan<span className="text-gold">Zingy</span></span>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-3">
                <button
                  data-testid="nav-community-btn"
                  onClick={() => navigate('/community')}
                  className="btn-secondary hidden sm:inline-flex"
                >
                  Community
                </button>
                <button
                  data-testid="nav-marketplace-btn"
                  onClick={() => navigate('/marketplace')}
                  className="btn-secondary"
                >
                  Marketplace
                </button>
                {user ? (
                  <button
                    data-testid="nav-dashboard-btn"
                    onClick={() => navigate(dashboardRoute())}
                    className="btn-primary"
                  >
                    Dashboard
                  </button>
                ) : (
                  <button
                    data-testid="nav-login-btn"
                    onClick={() => navigate('/login')}
                    className="btn-primary"
                  >
                    Sign In
                  </button>
                )}
              </div>
            </div>
          </div>
        </nav>

        {/* Hero content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center py-28 sm:py-36">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-5"
          >
            Movies you love.
            <br />
            <span className="text-gold">Royalties you earn.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-body text-base sm:text-xl text-slate-200 mb-3"
          >
            Back Tollywood, Bollywood, Kollywood &amp; Hollywood films from your couch.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="font-body text-sm sm:text-base text-slate-400 mb-8"
          >
            Ready to start? Create your account in seconds.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-3 justify-center max-w-xl mx-auto"
          >
            <button
              data-testid="hero-get-started-btn"
              onClick={() => navigate('/register')}
              className="btn-primary text-base px-8 py-4 flex items-center justify-center gap-2"
            >
              Get Started
              <CaretRight size={18} weight="bold" />
            </button>
            <button
              data-testid="hero-browse-movies-btn"
              onClick={() => navigate('/marketplace')}
              className="btn-secondary text-base px-8 py-4 flex items-center justify-center gap-2"
            >
              <Play size={18} weight="fill" />
              Browse Movies
            </button>
          </motion.div>
        </div>
      </section>

      {/* Trending Now */}
      {trending.length > 0 && (
        <section className="relative border-t border-white/5 py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-6">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold">
                TRENDING <span className="text-gold">NOW</span>
              </h2>
              <button
                data-testid="trending-see-all"
                onClick={() => navigate('/marketplace')}
                className="text-slate-400 hover:text-gold text-sm font-body uppercase tracking-wide flex items-center gap-1"
              >
                See all <CaretRight size={14} />
              </button>
            </div>
            <div
              data-testid="trending-row"
              className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hidden"
            >
              {trending.map((m, idx) => (
                <motion.button
                  key={m.id}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.04 }}
                  onClick={() => navigate(`/movie/${m.id}`)}
                  data-testid={`trending-card-${m.id}`}
                  className="relative flex-shrink-0 w-44 sm:w-56 aspect-[2/3] rounded-md overflow-hidden snap-start group"
                >
                  <img
                    src={m.poster_url}
                    alt={m.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                  <div className="absolute top-3 left-3 text-7xl font-heading font-extrabold text-gold/90 leading-none drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
                    {idx + 1}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-gold font-bold mb-1">
                      {m.industry}
                    </div>
                    <div className="font-heading text-base font-bold leading-tight">
                      {m.title}
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Reasons to Join */}
      <section className="relative border-t border-white/5 py-20 bg-card/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-10">
            MORE REASONS TO <span className="text-gold">JOIN</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {REASONS.map((r, i) => {
              const Icon = r.icon;
              return (
                <motion.div
                  key={r.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="relative overflow-hidden rounded-lg p-7 bg-gradient-to-br from-[#1a0a14] via-[#0a0814] to-black border border-white/5"
                  data-testid={`reason-card-${i}`}
                >
                  <div className="flex items-start justify-between mb-5">
                    <h3 className="font-heading text-lg font-bold pr-4 leading-snug">
                      {r.title}
                    </h3>
                    <Icon size={36} className="text-gold flex-shrink-0" weight="bold" />
                  </div>
                  <p className="font-body text-sm text-slate-400 leading-relaxed">{r.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative border-t border-white/5 py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-10 text-center">
            FREQUENTLY ASKED <span className="text-gold">QUESTIONS</span>
          </h2>
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {FAQS.map((item, i) => (
              <AccordionItem
                key={item.q}
                value={`item-${i}`}
                className="bg-card border border-white/5 rounded-md px-5 sm:px-7 data-[state=open]:border-gold/40 transition-colors"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="font-heading text-base sm:text-lg font-bold py-5 hover:no-underline text-left">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="font-body text-sm sm:text-base text-slate-300 leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="text-center mt-12">
            <p className="font-body text-base text-slate-400 mb-5">
              Ready to back your next favourite film?
            </p>
            <button
              data-testid="faq-cta-btn"
              onClick={() => navigate('/register')}
              className="btn-primary text-base px-10 py-4 inline-flex items-center gap-2"
            >
              Get Started
              <CaretRight size={18} weight="bold" />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src="/fanzingy-logo.jpeg" alt="FanZingy" className="h-10 w-10 rounded-full object-cover ring-1 ring-white/10" data-testid="brand-logo" />
            <span className="font-heading text-2xl font-bold tracking-tight">Fan<span className="text-gold">Zingy</span></span>
            </div>
            <p className="font-body text-xs text-slate-400 uppercase tracking-wide">
              © {new Date().getFullYear()} Fanzingy · Invest in cinema
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
